import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  DuplicateCustomer,
  DuplicateCustomerGroup,
  MergeMoneyMode,
  UserService,
} from '@beautyapp/core';

import { UserViewComponent } from '../../components/user-view/user-view.component';

/**
 * beaut-491, slice 1 — which customers share a phone number.
 *
 * READ ONLY. It shows candidates and opens records; it changes nothing.
 * Merging is a later slice, and until it exists this screen must not imply it
 * can do anything but show.
 *
 * A group is a CANDIDATE, not a verdict. A mother and daughter on one number
 * are two real customers and nothing here can tell them from a double entry,
 * so the screen says so rather than calling them duplicates outright.
 */
@Component({
  selector: 'beautyapp-duplicate-customers',
  templateUrl: './duplicate-customers.component.html',
  styleUrls: ['./duplicate-customers.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzInputModule,
    NzTableModule,
    NzCardModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzEmptyModule,
    NzAlertModule,
    NzToolTipModule,
    NzCheckboxModule,
    NzRadioModule,
    NzModalModule,
    NzDividerModule,
  ],
})
export class DuplicateCustomersComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private drawer = inject(NzDrawerService);

  loading = false;
  loadError = false;
  groups: DuplicateCustomerGroup[] = [];

  page = 1;
  pageSize = 20;
  /** GROUPS, not customers — the pager counts rows and a row is a group. */
  total = 0;

  ngOnInit(): void {
    this.loadSettings();
    this.load();
  }

  /** Bound to the search box: a phone number or a customer's name. */
  search = '';

  private searchTimer?: ReturnType<typeof setTimeout>;

  /**
   * Debounced, because the query groups every customer in the organisation and
   * firing it per keystroke would run it five times to answer once.
   */
  onSearchChange(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.runSearch(), 350);
  }

  /** Enter runs it now rather than waiting out the debounce. */
  runSearch(): void {
    clearTimeout(this.searchTimer);
    // Back to page 1: the result set changed, and page 4 of the old one is
    // very likely past the end of the new one.
    this.page = 1;
    this.expanded.clear();
    this.load();
  }

  clearSearch(): void {
    this.search = '';
    this.runSearch();
  }

  ngOnDestroy(): void {
    // A pending timer would fire into a destroyed component.
    clearTimeout(this.searchTimer);
  }

  /**
   * The term the numbers on screen actually came from — not what is currently
   * in the box.
   *
   * Reading `search` directly would relabel the summary as a search result the
   * moment a key is pressed, while the totals below it still describe the
   * previous, unsearched query.
   */
  private loadedSearch = '';

  get searched(): boolean {
    return this.loadedSearch !== '';
  }

  load(): void {
    this.loading = true;
    this.loadError = false;

    const term = this.search.trim();

    this.userService
      .findDuplicateGroups(this.page, this.pageSize, term)
      .subscribe({
        next: (res) => {
          this.loadedSearch = term;
          this.groups = res?.data ?? [];
          this.total = res?.meta?.itemCount ?? 0;
          this.loading = false;
        },
        error: () => {
          // Say so. An empty table on a failed request reads as "no
          // duplicates", which is the opposite of what happened and the more
          // comforting of the two — exactly the wrong way for a failure to
          // land.
          this.loadedSearch = term;
          this.groups = [];
          this.total = 0;
          this.loadError = true;
          this.loading = false;
        },
      });
  }

  /**
   * Which groups are open, by phone. Collapsed by default: one stage group
   * holds 98 customers, and expanding everything would bury the 77 group rows
   * the operator came to read.
   */
  expanded = new Set<string>();

  toggle(phone: string, open: boolean): void {
    if (open) {
      this.expanded.add(phone);
    } else {
      this.expanded.delete(phone);
    }
  }

  onPageChange(page: number): void {
    this.page = page;
    // Nothing on the new page is open yet, and a stale key would silently
    // expand an unrelated group if two pages ever shared a number.
    this.expanded.clear();
    this.load();
  }

  /** How many customers are on this page, across all its groups. */
  get customersOnPage(): number {
    return this.groups.reduce((sum, g) => sum + g.customers.length, 0);
  }

  displayName(customer: DuplicateCustomer): string {
    const name = [customer.firstname, customer.lastname]
      .filter(Boolean)
      .join(' ')
      .trim();

    // Never build the name inside a template literal with `?.` — an absent
    // value renders the word "undefined" instead of nothing.
    return name || 'უსახელო ჩანაწერი';
  }

  openCustomer(customer: DuplicateCustomer): void {
    this.drawer.create<UserViewComponent, { data: string }, string>({
      nzTitle: this.displayName(customer),
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: { padding: '0 15px 15px 15px' },
      nzData: { data: customer.id },
    });
  }

  /** Row identity for *ngFor, so paging does not re-create every row. */
  trackGroup(_: number, group: DuplicateCustomerGroup): string {
    return group.phone;
  }

  trackCustomer(_: number, customer: DuplicateCustomer): string {
    return customer.id;
  }

  // ── beaut-491 slice 2: merging ──────────────────────────────────────────────

  /** Whether this branch may merge at all. Off until the server says otherwise. */
  mergeEnabled = false;

  /**
   * Which record survives, per group. Empty until the operator picks: there is
   * no sensible default — "the oldest" is a guess about which of two records is
   * the real customer, and getting it wrong is what the merge cannot undo.
   */
  keepId: Record<string, string> = {};

  /** Which records are to be folded in, per group. */
  selected: Record<string, Set<string>> = {};

  /** The group whose confirmation is open, or null. */
  confirming: DuplicateCustomerGroup | null = null;

  merging = false;
  mergeError: string | null = null;
  mergeResult: { merged: number; moved: number } | null = null;

  moneyMode: MergeMoneyMode = 'SUM';
  moneySourceId: string | null = null;

  private loadSettings(): void {
    this.userService.getDuplicateSettings().subscribe({
      next: (res) => (this.mergeEnabled = res?.mergeEnabled === true),
      // Fails CLOSED. A merge screen offered by mistake is worse than one
      // missing by mistake — the second is a support call, the first is data.
      error: () => (this.mergeEnabled = false),
    });
  }

  chooseKeep(group: DuplicateCustomerGroup, id: string): void {
    this.keepId[group.phone] = id;
    // A record cannot be both kept and folded in; the server refuses that
    // combination, and the screen should never build it in the first place.
    this.selectionFor(group).delete(id);
  }

  toggleSelected(group: DuplicateCustomerGroup, id: string, on: boolean): void {
    const selection = this.selectionFor(group);

    if (on) {
      selection.add(id);
      if (this.keepId[group.phone] === id) {
        delete this.keepId[group.phone];
      }
    } else {
      selection.delete(id);
    }
  }

  selectionFor(group: DuplicateCustomerGroup): Set<string> {
    return (this.selected[group.phone] ??= new Set<string>());
  }

  isSelected(group: DuplicateCustomerGroup, id: string): boolean {
    return this.selectionFor(group).has(id);
  }

  canMerge(group: DuplicateCustomerGroup): boolean {
    return (
      this.mergeEnabled &&
      !!this.keepId[group.phone] &&
      this.selectionFor(group).size > 0
    );
  }

  /** The records in this merge — the survivor and everything folded in. */
  participants(group: DuplicateCustomerGroup): DuplicateCustomer[] {
    const selection = this.selectionFor(group);
    const keep = this.keepId[group.phone];

    return group.customers.filter((c) => c.id === keep || selection.has(c.id));
  }

  /** Those carrying a non-zero amount in any of the four columns. */
  moneyHolders(group: DuplicateCustomerGroup): DuplicateCustomer[] {
    return this.participants(group).filter(
      (c) => c.balance !== 0 || c.cashback !== 0 || c.debt !== 0 || c.minutes !== 0
    );
  }

  /** Whether the operator has to say what happens to the money. */
  needsMoneyChoice(group: DuplicateCustomerGroup): boolean {
    return this.moneyHolders(group).length > 1;
  }

  openConfirm(group: DuplicateCustomerGroup): void {
    this.confirming = group;
    this.mergeError = null;
    this.mergeResult = null;
    this.moneyMode = 'SUM';
    this.moneySourceId = this.keepId[group.phone] ?? null;
  }

  cancelConfirm(): void {
    this.confirming = null;
    // Released here too, or a cancelled confirmation leaves the button dead.
    this.merging = false;
  }

  keptRecord(group: DuplicateCustomerGroup): DuplicateCustomer | undefined {
    return group.customers.find((c) => c.id === this.keepId[group.phone]);
  }

  mergedRecords(group: DuplicateCustomerGroup): DuplicateCustomer[] {
    const selection = this.selectionFor(group);
    return group.customers.filter((c) => selection.has(c.id));
  }

  confirmMerge(): void {
    const group = this.confirming;
    if (!group || this.merging) return;

    this.merging = true;
    this.mergeError = null;

    const needsChoice = this.needsMoneyChoice(group);

    this.userService
      .mergeDuplicates({
        keepId: this.keepId[group.phone],
        mergeIds: [...this.selectionFor(group)],
        // Sent only when it is actually a choice. Sending a mode when there is
        // nothing to decide would record a decision nobody made.
        ...(needsChoice
          ? {
              moneyMode: this.moneyMode,
              ...(this.moneyMode === 'KEEP' && this.moneySourceId
                ? { moneySourceId: this.moneySourceId }
                : {}),
            }
          : {}),
      })
      .subscribe({
        next: (res) => {
          this.merging = false;
          this.confirming = null;
          this.mergeResult = {
            merged: res?.merged ?? 0,
            moved: Object.values(res?.movedCounts ?? {}).reduce(
              (sum, n) => sum + Number(n),
              0
            ),
          };
          delete this.keepId[group.phone];
          this.selected[group.phone] = new Set<string>();
          // The group is gone or smaller now; anything on screen is stale.
          this.load();
        },
        error: (err) => {
          this.merging = false;
          // The server's own sentence, which names what went wrong -- a shared
          // phone that stopped matching, a staff link, an undecided amount.
          // A generic "merge failed" would hide all three.
          this.mergeError =
            err?.error?.message ?? 'შერწყმა ვერ შესრულდა. სცადეთ თავიდან.';
        },
      });
  }
}
